import {
  LAYERS_NORMALIZED_MAX,
  normalizedToPixels,
  pixelsToNormalized,
} from "../../components/Modal/ModalImageEdit/layers/layerGeometry";
import { VIDEO_EDITOR_DEFAULT_COMPOSITION } from "./constants";

export const CLIP_LAYOUT_NORMALIZED_MAX = LAYERS_NORMALIZED_MAX;

export const FULL_CANVAS_CLIP_LAYOUT = {
  x: 0,
  y: 0,
  width: CLIP_LAYOUT_NORMALIZED_MAX,
  height: CLIP_LAYOUT_NORMALIZED_MAX,
};

export const createFullCanvasClipLayout = () => ({ ...FULL_CANVAS_CLIP_LAYOUT });

const MIN_LAYOUT_SIZE = 40;
const DEFAULT_IMAGE_SIZE_RATIO = 0.4;
const ASPECT_RATIO_EPSILON = 0.01;

const getCompositionSize = ({ compositionWidth, compositionHeight } = {}) => ({
  width:
    Number(compositionWidth) > 0
      ? Number(compositionWidth)
      : VIDEO_EDITOR_DEFAULT_COMPOSITION.width,
  height:
    Number(compositionHeight) > 0
      ? Number(compositionHeight)
      : VIDEO_EDITOR_DEFAULT_COMPOSITION.height,
});

const getMinPixelSize = ({ compositionWidth, compositionHeight }) =>
  Math.max(
    1,
    (MIN_LAYOUT_SIZE / CLIP_LAYOUT_NORMALIZED_MAX) *
      Math.min(compositionWidth, compositionHeight)
  );

export const getSourceAspectRatio = ({ sourceWidth, sourceHeight } = {}) => {
  const width = Number(sourceWidth);
  const height = Number(sourceHeight);

  if (width > 0 && height > 0) {
    return width / height;
  }

  return null;
};

export const hasSourceDimensions = ({ sourceWidth, sourceHeight } = {}) =>
  Number(sourceWidth) > 0 && Number(sourceHeight) > 0;

const fitPixelBoxToAspect = ({ box, aspectRatio }) => {
  const aspect = Number(aspectRatio);
  if (!(aspect > 0) || !(box.width > 0) || !(box.height > 0)) {
    return box;
  }

  const currentAspect = box.width / box.height;
  if (Math.abs(currentAspect - aspect) < ASPECT_RATIO_EPSILON) {
    return box;
  }

  const centerX = box.x + box.width / 2;
  const centerY = box.y + box.height / 2;
  let width = box.width;
  let height = width / aspect;

  if (height > box.height) {
    height = box.height;
    width = height * aspect;
  }

  return {
    x: centerX - width / 2,
    y: centerY - height / 2,
    width,
    height,
  };
};

export const clampLayoutToArtboard = (layout = {}) => {
  const width = Math.max(
    MIN_LAYOUT_SIZE,
    Math.min(Number(layout.width) || 0, CLIP_LAYOUT_NORMALIZED_MAX)
  );
  const height = Math.max(
    MIN_LAYOUT_SIZE,
    Math.min(Number(layout.height) || 0, CLIP_LAYOUT_NORMALIZED_MAX)
  );
  const x = Math.max(
    0,
    Math.min(Number(layout.x) || 0, CLIP_LAYOUT_NORMALIZED_MAX - width)
  );
  const y = Math.max(
    0,
    Math.min(Number(layout.y) || 0, CLIP_LAYOUT_NORMALIZED_MAX - height)
  );

  return { x, y, width, height };
};

export const isFullCanvasClipLayout = (layout) => {
  if (!layout || typeof layout !== "object") {
    return false;
  }

  const normalized = clampLayoutToArtboard(layout);
  return (
    normalized.x === 0 &&
    normalized.y === 0 &&
    normalized.width === CLIP_LAYOUT_NORMALIZED_MAX &&
    normalized.height === CLIP_LAYOUT_NORMALIZED_MAX
  );
};

export const clampPixelBoxToRect = ({
  x,
  y,
  width,
  height,
  rect,
  minSize = MIN_LAYOUT_SIZE,
  inset = 0,
  aspectRatio,
} = {}) => {
  const bounds = {
    x: rect.x + inset,
    y: rect.y + inset,
    width: Math.max(0, rect.width - inset * 2),
    height: Math.max(0, rect.height - inset * 2),
  };

  if (!bounds.width || !bounds.height) {
    return { x: bounds.x, y: bounds.y, width: minSize, height: minSize };
  }

  const aspect = Number(aspectRatio);
  if (!(aspect > 0)) {
    const nextWidth = Math.max(minSize, Math.min(width, bounds.width));
    const nextHeight = Math.max(minSize, Math.min(height, bounds.height));
    const maxX = bounds.x + bounds.width - nextWidth;
    const maxY = bounds.y + bounds.height - nextHeight;

    return {
      x: Math.max(bounds.x, Math.min(x, maxX)),
      y: Math.max(bounds.y, Math.min(y, maxY)),
      width: nextWidth,
      height: nextHeight,
    };
  }

  let nextWidth = Math.max(Number(width) || 0, 1);
  let nextHeight = nextWidth / aspect;
  const minScale = Math.max(minSize / nextWidth, minSize / nextHeight, 0);
  if (minScale > 1) {
    nextWidth *= minScale;
    nextHeight *= minScale;
  }

  const fitScale = Math.min(
    1,
    bounds.width / nextWidth,
    bounds.height / nextHeight
  );
  nextWidth *= fitScale;
  nextHeight *= fitScale;

  return {
    x: Math.max(
      bounds.x,
      Math.min(x, bounds.x + bounds.width - nextWidth)
    ),
    y: Math.max(
      bounds.y,
      Math.min(y, bounds.y + bounds.height - nextHeight)
    ),
    width: nextWidth,
    height: nextHeight,
  };
};

export const layoutToCompositionPixels = ({
  layout,
  compositionWidth,
  compositionHeight,
}) =>
  normalizedToPixels({
    x: layout.x,
    y: layout.y,
    width: layout.width,
    height: layout.height,
    displayWidth: compositionWidth,
    displayHeight: compositionHeight,
  });

export const compositionPixelsToLayout = ({
  x,
  y,
  width,
  height,
  compositionWidth,
  compositionHeight,
  aspectRatio,
}) => {
  const clamped = clampPixelBoxToRect({
    x,
    y,
    width,
    height,
    rect: {
      x: 0,
      y: 0,
      width: compositionWidth,
      height: compositionHeight,
    },
    minSize: getMinPixelSize({ compositionWidth, compositionHeight }),
    aspectRatio,
  });

  const normalized = pixelsToNormalized({
    ...clamped,
    displayWidth: compositionWidth,
    displayHeight: compositionHeight,
  });

  return {
    x: normalized.x,
    y: normalized.y,
    width: normalized.width,
    height: normalized.height,
  };
};

export const createDefaultImageClipLayout = ({
  sourceWidth,
  sourceHeight,
  compositionWidth,
  compositionHeight,
} = {}) => {
  const composition = getCompositionSize({
    compositionWidth,
    compositionHeight,
  });
  const sourceAspect = getSourceAspectRatio({ sourceWidth, sourceHeight });
  const aspect =
    sourceAspect > 0 ? sourceAspect : composition.width / composition.height;
  const maxPx = Math.min(composition.width, composition.height) * DEFAULT_IMAGE_SIZE_RATIO;
  let widthPx = maxPx;
  let heightPx = maxPx;

  if (aspect >= 1) {
    heightPx = maxPx / aspect;
  } else {
    widthPx = maxPx * aspect;
  }

  return compositionPixelsToLayout({
    x: (composition.width - widthPx) / 2,
    y: (composition.height - heightPx) / 2,
    width: widthPx,
    height: heightPx,
    compositionWidth: composition.width,
    compositionHeight: composition.height,
    aspectRatio: aspect,
  });
};

export const layoutToPercentFields = (layout = {}) => {
  const normalized = clampLayoutToArtboard(layout);
  const toPercent = (value) =>
    Math.round(((Number(value) || 0) / CLIP_LAYOUT_NORMALIZED_MAX) * 1000) / 10;

  return {
    x: toPercent(normalized.x),
    y: toPercent(normalized.y),
    width: toPercent(normalized.width),
    height: toPercent(normalized.height),
  };
};

export const percentFieldsToLayout = ({
  x,
  y,
  width,
  height,
  currentLayout,
} = {}) => {
  const current = layoutToPercentFields(currentLayout);
  const toNormalized = (value, fallback) => {
    const next = Number(value);
    const source = Number.isFinite(next) ? next : fallback;
    return (source / 100) * CLIP_LAYOUT_NORMALIZED_MAX;
  };

  return clampLayoutToArtboard({
    x: toNormalized(x, current.x),
    y: toNormalized(y, current.y),
    width: toNormalized(width, current.width),
    height: toNormalized(height, current.height),
  });
};

export const layoutToPercentStyle = (layout = {}) => {
  const normalized = clampLayoutToArtboard(layout);
  const toPercent = (value) =>
    `${((Number(value) || 0) / CLIP_LAYOUT_NORMALIZED_MAX) * 100}%`;

  return {
    left: toPercent(normalized.x),
    top: toPercent(normalized.y),
    width: toPercent(normalized.width),
    height: toPercent(normalized.height),
  };
};

export const layoutToLayerBoxStyle = (layout) => {
  const percent = layoutToPercentStyle(layout || FULL_CANVAS_CLIP_LAYOUT);
  return {
    position: "absolute",
    left: percent.left,
    top: percent.top,
    width: percent.width,
    height: percent.height,
    right: "auto",
    bottom: "auto",
  };
};

export const isValidClipLayout = (layout) => {
  if (!layout || typeof layout !== "object") {
    return false;
  }

  const { x, y, width, height } = layout;
  const values = [x, y, width, height];

  if (!values.every((value) => Number.isFinite(Number(value)))) {
    return false;
  }

  const normalized = clampLayoutToArtboard({
    x: Number(x),
    y: Number(y),
    width: Number(width),
    height: Number(height),
  });

  return (
    normalized.x >= 0 &&
    normalized.y >= 0 &&
    normalized.width >= MIN_LAYOUT_SIZE &&
    normalized.height >= MIN_LAYOUT_SIZE &&
    normalized.x + normalized.width <= CLIP_LAYOUT_NORMALIZED_MAX &&
    normalized.y + normalized.height <= CLIP_LAYOUT_NORMALIZED_MAX
  );
};

export const repairLayoutAspectRatio = ({
  layout,
  sourceWidth,
  sourceHeight,
  compositionWidth,
  compositionHeight,
  fallbackLayout,
} = {}) => {
  const composition = getCompositionSize({
    compositionWidth,
    compositionHeight,
  });
  const aspectRatio = getSourceAspectRatio({ sourceWidth, sourceHeight });

  if (!isValidClipLayout(layout)) {
    if (isValidClipLayout(fallbackLayout)) {
      return clampLayoutToArtboard(fallbackLayout);
    }

    return createDefaultImageClipLayout({
      sourceWidth,
      sourceHeight,
      compositionWidth: composition.width,
      compositionHeight: composition.height,
    });
  }

  if (!(aspectRatio > 0) || isFullCanvasClipLayout(layout)) {
    return clampLayoutToArtboard(layout);
  }

  const pixels = layoutToCompositionPixels({
    layout,
    compositionWidth: composition.width,
    compositionHeight: composition.height,
  });
  const fitted = fitPixelBoxToAspect({ box: pixels, aspectRatio });

  return compositionPixelsToLayout({
    ...fitted,
    compositionWidth: composition.width,
    compositionHeight: composition.height,
    aspectRatio,
  });
};
